import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { queryKeys } from '@/lib/queryClient';
import {
  fraudApi,
  type FraudReportsQuery,
  type UnblockRequestsQuery,
  type BlockCustomerPayload,
  type ProcessUnblockPayload,
  type DriverFraudStatus,
} from '../api/fraud.api';

export function useFraudReports(query: FraudReportsQuery = {}) {
  return useQuery({
    queryKey: queryKeys.fraud.reports(query as Record<string, unknown>),
    queryFn: () => fraudApi.getReports(query),
    staleTime: 30 * 1000,
  });
}

export function useCustomerFraudReports(customerId: string) {
  return useQuery({
    queryKey: queryKeys.fraud.customerReports(customerId),
    queryFn: () => fraudApi.getCustomerReports(customerId),
    enabled: !!customerId,
  });
}

export function useCustomerBlockHistory(customerId: string) {
  return useQuery({
    queryKey: queryKeys.fraud.customerBlocks(customerId),
    queryFn: () => fraudApi.getBlockHistory(customerId),
    enabled: !!customerId,
  });
}

export function useUnblockRequests(query: UnblockRequestsQuery = {}) {
  return useQuery({
    queryKey: queryKeys.fraud.unblockRequests(query as Record<string, unknown>),
    queryFn: () => fraudApi.getUnblockRequests(query),
    staleTime: 30 * 1000,
  });
}

export function useResolveReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => fraudApi.resolveReport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.fraud.all });
      toast.success('Report marked as resolved');
    },
  });
}

export function useBlockCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, payload }: { customerId: string; payload: BlockCustomerPayload }) =>
      fraudApi.blockCustomer(customerId, payload),
    onSuccess: (_, { customerId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.fraud.customerBlocks(customerId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.detail(customerId) });
      toast.success('Customer blocked successfully');
    },
  });
}

export function useUnblockCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (customerId: string) => fraudApi.unblockCustomer(customerId),
    onSuccess: (_, customerId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.fraud.customerBlocks(customerId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.detail(customerId) });
      toast.success('Customer unblocked successfully');
    },
  });
}

export function useProcessUnblockRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ blockId, payload }: { blockId: string; payload: ProcessUnblockPayload }) =>
      fraudApi.processUnblockRequest(blockId, payload),
    onSuccess: (_, { payload }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.fraud.all });
      toast.success(payload.status === 'approved' ? 'Unblock request approved' : 'Unblock request rejected');
    },
  });
}

// ─── Intelligence hooks ───────────────────────────────────────────────────────

export function useFraudIntelligenceOverview() {
  return useQuery({
    queryKey: queryKeys.fraud.intelligenceOverview(),
    queryFn: () => fraudApi.getIntelligenceOverview(),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

export function useDriverRiskScores(limit = 20) {
  return useQuery({
    queryKey: queryKeys.fraud.driverRiskScores(limit),
    queryFn: () => fraudApi.getDriverRiskScores(limit),
    staleTime: 60 * 1000,
  });
}

export function useFlaggedBookings() {
  return useQuery({
    queryKey: queryKeys.fraud.flaggedBookings(),
    queryFn: () => fraudApi.getFlaggedBookings(),
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
  });
}

export function useDriverIncidents(filters: { status?: DriverFraudStatus; driver_id?: string; limit?: number; offset?: number } = {}) {
  return useQuery({
    queryKey: queryKeys.fraud.driverIncidents(filters as Record<string, unknown>),
    queryFn: () => fraudApi.listDriverIncidents(filters),
    staleTime: 30 * 1000,
  });
}

export function useConfirmDriverIncident() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      fraudApi.confirmDriverIncident(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.fraud.all });
      toast.success('Fraud confirmed — driver banned and booking cancelled');
    },
  });
}

export function useClearDriverIncident() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      fraudApi.clearDriverIncident(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.fraud.all });
      toast.success('Incident cleared — driver wallet unfrozen');
    },
  });
}
